import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTenantHealth, type TenantHealthResponse } from "@/lib/api";
import { Badge, Button, Card, ErrorBanner, PageContainer, toErrorMessage } from "@/components/Forms";
import { RefreshIcon } from "@/components/Icons";

export default function HealthPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<TenantHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getTenantHealth()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("health.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  return (
    <PageContainer
      title={t("health.title")}
      subtitle={t("health.subtitle")}
      actions={
        <Button onClick={() => setReloadAt((n) => n + 1)}>
          <RefreshIcon size={14} /> {t("forms.refresh")}
        </Button>
      }
    >
      <ErrorBanner message={error} />

      <Card title={t("health.title")} description={data?.workerId ? `worker_id=${data.workerId}` : undefined}>
        {data === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : data.tenants.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("health.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-2">{t("health.colTenant")}</th>
                  <th className="text-left px-2 py-2">{t("health.colStatus")}</th>
                  <th className="text-right px-2 py-2">{t("health.colDue")}</th>
                  <th className="text-left px-2 py-2 hidden md:table-cell">{t("tenantDetail.poolTitle")}</th>
                  <th className="text-right px-2 py-2 hidden md:table-cell">
                    {t("health.colPoolActive")} / {t("tenantDetail.poolMax")}
                  </th>
                  <th className="text-right px-2 py-2 hidden md:table-cell">{t("tenantDetail.poolIdle")}</th>
                  <th className="text-left px-2 py-2 hidden lg:table-cell">{t("health.colLastTick")}</th>
                  <th className="text-right px-2 py-2 hidden lg:table-cell">{t("health.colLastTickDuration")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.tenants.map((tt) => (
                  <tr key={tt.slug} className="hover:bg-muted/30">
                    <td className="px-2 py-2 font-mono">
                      <Link to={`/tenants/${tt.slug}`} className="text-accent hover:underline">
                        {tt.slug}
                      </Link>
                    </td>
                    <td className="px-2 py-2">
                      <Badge tone={tt.status === "UP" ? "success" : "danger"}>{tt.status}</Badge>
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{tt.dueCount ?? 0}</td>
                    <td className="px-2 py-2 hidden md:table-cell font-mono text-xs">{tt.pool?.name ?? "—"}</td>
                    <td className="px-2 py-2 hidden md:table-cell text-right font-mono">
                      {tt.pool ? `${tt.pool.active ?? 0} / ${tt.pool.total ?? 0}` : "—"}
                    </td>
                    <td className="px-2 py-2 hidden md:table-cell text-right font-mono">
                      {tt.pool?.waiting ?? 0}
                    </td>
                    <td className="px-2 py-2 hidden lg:table-cell text-xs text-muted-foreground">
                      {tt.lastTickAt ?? "—"}
                    </td>
                    <td className="px-2 py-2 hidden lg:table-cell text-right font-mono text-xs">
                      {tt.lastTickDurationMs ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
}
